"use client";

import defaultAvatar from "@/assets/global/default-avatar.webp";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SmartImage } from "@/components/ui/smart-image";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { getBetTypeDisplay } from "@/constants/bet-type-style";
import { useThemeMode } from "@/features/theme/theme-provider";
import { getBrandLogoIcon, getBrandLogoIconDark } from "@/lib/brand-config";
import { cn } from "@/lib/utils";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useReplyDialog } from "./hooks";
import type { CommentData } from "./types";

interface ReplyDialogProps {
    open: boolean;
    onOpenChange: ( open: boolean ) => void;
    eventData: { eventImageUrl?: string; eventTitle: string; position?: "Yes" | "No" | "No Position" };
    targetComment: CommentData;
    onReplySubmit: ( content: string ) => Promise<void> | void;
    submitting?: boolean;
    matchups?: any[];
}

export default function ReplyDialog(
    { open, onOpenChange, eventData, targetComment, onReplySubmit, submitting = false, matchups }: ReplyDialogProps,
) {
    const { replyContent, remainingChars, textAreaRef, handleChange, handleSubmit, handleClose } = useReplyDialog( {
        targetComment,
        onSubmit: onReplySubmit,
        onClose: () => onOpenChange( false ),
    } );
    const { themeMode } = useThemeMode();
    const fallbackCoverImage = themeMode === "dark" ? getBrandLogoIconDark() : getBrandLogoIcon();

    // Calculate effective position: prefer eventData.position, fallback to targetComment.position
    const betTypeDisplay = getBetTypeDisplay( targetComment.betType, matchups );
    const shouldShowPosition = !betTypeDisplay.isNeutral;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                showCloseButton={false}
                className="inset-x-0 bottom-0 w-full max-w-none p-0 max-h-[calc(100vh-56px)] min-h-0 flex flex-col overflow-hidden rounded-t-2xl"
            >
                <VisuallyHidden asChild>
                    <SheetTitle>{eventData.eventTitle}</SheetTitle>
                </VisuallyHidden>

                <div className="flex flex-col flex-1 min-h-0">
                    <div className="flex items-center py-3 shrink-0 px-4">
                        <button
                            onClick={handleClose}
                            className="inline-flex items-center justify-center size-6 text-t1 hover:opacity-70 transition-opacity"
                        >
                            <ArrowLeft className="w-6 h-6" />
                            <VisuallyHidden>Back</VisuallyHidden>
                        </button>
                    </div>

                    <div className="px-4 pb-4 shrink-0">
                        <div className="flex items-center">
                            <div className="flex items-center justify-center min-w-12 rounded-lg shrink-0 size-12">
                                <div className="relative rounded-lg shrink-0 size-12">
                                    <div className="absolute inset-0 overflow-hidden rounded-lg">
                                        <SmartImage
                                            src={eventData.eventImageUrl || fallbackCoverImage}
                                            alt={eventData.eventTitle}
                                            className="w-full h-full object-cover"
                                            imageUrlOptions={{ width: 150, height: 150 }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="shrink-0 w-4" />

                            <div>
                                <div className="flex flex-col font-medium text-sm leading-[22px] text-t1">
                                    <p className="whitespace-pre-wrap break-all">{eventData.eventTitle}</p>
                                </div>
                                {shouldShowPosition && (
                                    <div
                                        className={cn(
                                            "inline-flex h-6 items-center justify-center rounded-[5px] px-2 text-xs font-medium",
                                            betTypeDisplay.badgeClass,
                                        )}
                                    >
                                        {betTypeDisplay.label}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-4">
                        <div className="border-b border-stroke pb-4">
                            <div className="flex gap-4">
                                <div className="shrink-0">
                                    <div className="w-12 h-12 rounded-lg bg-surface-disabled overflow-hidden">
                                        <SmartImage
                                            src={targetComment.userAvatar || defaultAvatar}
                                            alt={targetComment.userName}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center mb-1">
                                        <div className="flex items-center gap-2 h-6">
                                            <span className="text-sm font-semibold leading-6 text-t1">
                                                {targetComment.userName}
                                            </span>
                                            <span className="text-xs leading-5 text-t3">{targetComment.time}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[14.9px] leading-6 tracking-[0.15px] text-t1 whitespace-pre-wrap break-all">
                                            {targetComment.content}
                                        </p>
                                    </div>

                                    <div className="mt-1">
                                        <p className="text-xs leading-5">
                                            <span className="text-t3">Replying to</span>
                                            <span className="text-data-primary ml-1">@{targetComment.userName}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="px-4 pb-6 pt-3 shrink-0 bg-surface-card">
                        <textarea
                            ref={textAreaRef}
                            value={replyContent}
                            onChange={( e ) => handleChange( e.target.value )}
                            placeholder="What's your prediction?"
                            className="w-full text-sm leading-6 tracking-[0.15px] text-t1 placeholder:text-t3 resize-none outline-none bg-transparent min-h-12 max-h-[192px] overflow-y-auto"
                            rows={1}
                        />

                        <div className="flex items-center justify-end gap-4 pt-3">
                            <span className="text-xs leading-8 tracking-[0.13px] text-t3">{remainingChars} left</span>
                            <button
                                onClick={handleSubmit}
                                disabled={!replyContent.trim() || submitting}
                                className="inline-flex items-center justify-center gap-2 bg-action-primary text-content-fixed-inverse text-sm leading-6 tracking-[0.15px] font-medium rounded-lg px-4 py-3 min-h-12 hover:bg-no-hover transition-colors disabled:opacity-20 disabled:cursor-not-allowed min-w-[72px]"
                            >
                                {submitting && <Loader2 className="animate-spin size-[1em]" />}
                                {submitting ? "Sending..." : "Reply"}
                            </button>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
